/**
 * CrmCreateDrawer — slide-over used to open a ticket.
 *
 * Opened from the order detail page (prefilled with that order, its store
 * and customer) and from the tickets queue, where there is no order yet:
 * a day-close variance or an audit finding is a real ticket with no order
 * attached, and previously the only way in was through an order page.
 *
 * A ticket nobody owns sits in nobody's inbox, so the drawer also assigns
 * a primary owner at creation — defaulting to whoever is filing it.
 *
 * The drawer mirrors the existing `MobileSidebarDrawer` pattern (radix
 * Dialog used as a side sheet) so we don't introduce a new primitive.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { LifeBuoy, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/auth/AuthProvider';
import {
  TICKET_PRIORITY_LABEL,
  TICKET_TYPE_LABEL,
} from '@/lib/crmStatus';
import { cn } from '@/lib/cn';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import {
  useAssignCrmTicketById,
  useCreateCrmTicket,
  useCrmAssignees,
  type CrmTicketPriority,
  type CrmTicketType,
} from '@/api/bi/crm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface CrmCreateDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefilled order, or null when the ticket is being opened standalone. */
  orderId: number | null;
  storeId?: number | null;
  customerId?: number | null;
  defaultType?: CrmTicketType;
  defaultSummary?: string;
}

const TYPE_OPTIONS: CrmTicketType[] = [
  'sla_breach',
  'customer_delay',
  'cancellation',
  'refund',
  'shrinkage',
];
const PRIORITY_OPTIONS: CrmTicketPriority[] = ['low', 'normal', 'high', 'critical'];

/** Types that only make sense against an order. */
const ORDER_REQUIRED_TYPES = new Set<CrmTicketType>([
  'sla_breach',
  'customer_delay',
  'cancellation',
  'refund',
]);

function defaultPriorityFor(type: CrmTicketType): CrmTicketPriority {
  return type === 'refund' ? 'high' : 'normal';
}

export function CrmCreateDrawer({
  open,
  onOpenChange,
  orderId,
  storeId,
  customerId,
  defaultType = 'customer_delay',
  defaultSummary = '',
}: CrmCreateDrawerProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const create = useCreateCrmTicket();
  const assign = useAssignCrmTicketById();

  const [type, setType] = useState<CrmTicketType>(defaultType);
  const [priority, setPriority] = useState<CrmTicketPriority>(defaultPriorityFor(defaultType));
  const [summary, setSummary] = useState(defaultSummary);
  const [orderInput, setOrderInput] = useState('');
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerQuery, setOwnerQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const debouncedOwnerQuery = useDebouncedValue(ownerQuery.trim(), 250);
  const assignees = useCrmAssignees(debouncedOwnerQuery);

  // Re-seed when the drawer opens so order-specific defaults (e.g. paid
  // cancel → refund) apply even if the component stayed mounted.
  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setPriority(defaultPriorityFor(defaultType));
    setSummary(defaultSummary);
    setOrderInput('');
    setOwnerId(user?.id != null ? String(user.id) : null);
    setOwnerQuery('');
    setError(null);
  }, [open, defaultType, defaultSummary, user?.id]);

  const ownerOptions = useMemo(() => {
    const seen = new Map<string, { value: string; label: string; hint?: string }>();
    if (user?.id != null) {
      seen.set(String(user.id), {
        value: String(user.id),
        label: `${user.name || user.username || 'Me'} (me)`,
        hint: user.role ?? undefined,
      });
    }
    for (const u of assignees.data?.results ?? []) {
      seen.set(String(u.id), { value: String(u.id), label: u.name, hint: u.role ?? undefined });
    }
    return Array.from(seen.values());
  }, [assignees.data, user?.id, user?.name, user?.username, user?.role]);

  const effectiveOrderId = orderId ?? (orderInput.trim() ? Number(orderInput.trim()) : null);
  const needsOrder = ORDER_REQUIRED_TYPES.has(type) && effectiveOrderId == null;

  async function handleSubmit() {
    if (!summary.trim()) {
      setError('Give the ticket a one-line summary — it is what the queue shows.');
      return;
    }
    if (needsOrder) {
      setError(`${TICKET_TYPE_LABEL[type]} tickets need an order number.`);
      return;
    }
    setError(null);
    try {
      const ticket = await create.mutateAsync({
        type,
        order_id: effectiveOrderId,
        store_id: storeId ?? null,
        customer_id: customerId ?? null,
        priority,
        summary: summary.trim(),
      });

      if (ownerId) {
        const userIds = [Number(ownerId)].filter((n) => Number.isFinite(n));
        try {
          await assign.mutateAsync({
            ticketId: ticket.id,
            user_ids: userIds,
            primary: userIds[0] ?? null,
          });
        } catch (err) {
          // The ticket exists — say so, and say why it is unowned.
          notify.warning(
            apiErrorMessage(err, `Ticket #${ticket.id} created, but the owner was not set.`),
          );
        }
      }

      notify.success(`Ticket #${ticket.id} created.`);
      onOpenChange(false);
      navigate(`/crm/tickets/${ticket.id}`);
    } catch (err) {
      const message = apiErrorMessage(err, 'Could not create the ticket.');
      setError(message);
      notify.error(message);
    }
  }

  const isSubmitting = create.isPending || assign.isPending;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => (isSubmitting ? null : onOpenChange(next))}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'duration-200 ease-out',
          )}
        />
        <DialogPrimitive.Content
          aria-label="Create CRM ticket"
          className={cn(
            'fixed inset-y-0 right-0 z-[1010] flex w-[420px] max-w-full flex-col overflow-y-auto border-l border-border bg-surface px-5 py-4 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-240 ease-out',
          )}
        >
          <DialogPrimitive.Title asChild>
            <h2 className="font-display inline-flex items-center gap-2 text-base font-semibold">
              <LifeBuoy className="h-4 w-4" />
              New CRM ticket
            </h2>
          </DialogPrimitive.Title>
          <DialogPrimitive.Close
            aria-label="Close"
            className="press absolute right-2 top-2 rounded-md p-1.5 text-muted-foreground hover:text-foreground focus-ring"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
          <p className="mt-1 text-xs text-muted-foreground">
            {orderId != null ? (
              <>
                Tagged to order <span className="font-mono text-foreground">#{orderId}</span>.
                Customer + store snapshot is captured automatically.
              </>
            ) : (
              <>Attach an order below, or leave it empty for shrinkage and audit findings.</>
            )}
          </p>

          <div className="mt-4 flex-1 space-y-3">
            <div className="space-y-1">
              <Label htmlFor="crm-create-type" className="text-[11px] uppercase tracking-wide">
                Type
              </Label>
              <Select
                value={type}
                onValueChange={(v) => {
                  const next = v as CrmTicketType;
                  setType(next);
                  setPriority(defaultPriorityFor(next));
                  setError(null);
                }}
              >
                <SelectTrigger id="crm-create-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TICKET_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {orderId == null ? (
              <div className="space-y-1">
                <Label htmlFor="crm-create-order" className="text-[11px] uppercase tracking-wide">
                  Order number {ORDER_REQUIRED_TYPES.has(type) ? '' : '(optional)'}
                </Label>
                <input
                  id="crm-create-order"
                  value={orderInput}
                  onChange={(e) => {
                    setOrderInput(e.target.value.replace(/[^\d]/g, ''));
                    setError(null);
                  }}
                  inputMode="numeric"
                  placeholder="e.g. 84213"
                  className="h-9 w-full rounded-md border border-input bg-surface px-3 text-xs"
                />
              </div>
            ) : null}

            <div className="space-y-1">
              <Label htmlFor="crm-create-priority" className="text-[11px] uppercase tracking-wide">
                Priority
              </Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as CrmTicketPriority)}>
                <SelectTrigger id="crm-create-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {TICKET_PRIORITY_LABEL[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="crm-create-summary" className="text-[11px] uppercase tracking-wide">
                Short summary
              </Label>
              <input
                id="crm-create-summary"
                value={summary}
                onChange={(e) => {
                  setSummary(e.target.value);
                  setError(null);
                }}
                placeholder="Customer called about delivery delay…"
                className="h-9 w-full rounded-md border border-input bg-surface px-3 text-xs"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wide">Primary owner</Label>
              <Combobox
                value={ownerId}
                onChange={setOwnerId}
                onSearchChange={setOwnerQuery}
                options={ownerOptions}
                placeholder="Nobody (lands in no inbox)"
                clearLabel="Nobody"
                emptyMessage="No matching staff."
                triggerClassName="w-full"
                width={360}
                loading={assignees.isLoading}
              />
              <p className="text-[11px] text-muted-foreground">
                The owner gets it in their CRM inbox. Leave empty only if someone else will triage.
              </p>
            </div>

            {error ? <p className="text-[11px] text-danger">{error}</p> : null}

            <p className="text-[11px] text-muted-foreground">
              You can attach call recordings, escalate, and (for refunds) tag products on the
              ticket detail page.
            </p>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-3">
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="sm" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogPrimitive.Close>
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create ticket'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
